import type { ReactElement, RefObject } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import {
    measureElement,
    observeElementOffset,
    observeElementRect,
    observeWindowRect,
    useVirtualizer,
} from '@tanstack/react-virtual';

import { useLocation } from '@luna/router';

import {
    getParentNode,
    getParentViewportStyle,
    getSpacerElementStyle,
    getWindowScrollOffset,
} from './VirtualScrollService';
import type { Measurement, VirtualScrollRestorationCache } from './VirtualScrollRestorationService';
import { defaultVirtualScrollRestorationCache, getOffset } from './VirtualScrollRestorationService';

interface HookOptions {
    getItemSize: (index: number) => number;
    hasNextPage?: boolean;
    height?: number | CSSStyleDeclaration['height'];
    isNextPageLoading?: boolean;
    items: ReactElement[];
    loadMoreItems?(): Promise<unknown>;
    overscan?: number;
    spacerSelector?: string;
    maxHeight?: number | CSSStyleDeclaration['height'];
    cache?: VirtualScrollRestorationCache;
    shouldRestoreScroll: boolean;
    smoothScrollRestoration: boolean;
    onOffsetSave?(): number;
}

interface HookResult {
    parentRef: RefObject<HTMLDivElement | null>;
    spacerRef: RefObject<HTMLElement | null>;
    virtualItems: VirtualItem[];
}

const NOOP = () => {};

// should be ScrollElement = Window | HTMLElement
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ScrollElement = any;

type Handler = (offset: number, isScrolling: boolean) => void | undefined;

/**
 * You can use this class to override some html element to act as a whole window scroll container
 * Use in special cases only, for example when the whole app is in position: fixed container
 */
function getVirtualScrollWindowElement() {
    return document.querySelector<HTMLElement>('.virtual-scroll-window');
}

export function useVirtualScroll({
    getItemSize,
    height,
    hasNextPage,
    items,
    overscan,
    isNextPageLoading,
    loadMoreItems,
    spacerSelector,
    cache = defaultVirtualScrollRestorationCache,
    shouldRestoreScroll,
    smoothScrollRestoration,
    onOffsetSave,
}: HookOptions): HookResult {
    const isUsingContainer = height !== undefined;
    const isUsingScrollWindow = !!getVirtualScrollWindowElement();
    const shouldUseWindowAsScrollableElement = !isUsingContainer && !isUsingScrollWindow;

    const parentRef = useRef<HTMLDivElement | null>(null);
    const spacerRef = useRef<HTMLElement | null>(null);
    const setScrollOffsetRef = useRef<Handler | null>(null);
    const shouldUseWindowAsScrollableElementRef = useRef(shouldUseWindowAsScrollableElement);
    shouldUseWindowAsScrollableElementRef.current = shouldUseWindowAsScrollableElement;

    const { current: observeWindowOffset } = useRef(
        (
            _: Virtualizer<ScrollElement, HTMLElement>,
            cb: (offset: number, isScrolling: boolean) => void,
        ) => {
            // we will store the offset setter callback to ref, which we can reference in our scroll tracking effect
            setScrollOffsetRef.current = cb;
        },
    );

    const { pathname: scrollRestorationKey } = useLocation();
    const scrollRestoreSnapshot = cache.get(scrollRestorationKey);

    const isScrollRestoreSnapshotValid = useMemo(() => {
        if (!scrollRestoreSnapshot) {
            return false;
        }

        for (let i = 0; i < scrollRestoreSnapshot.measurements.length; i += 1) {
            const measurement = scrollRestoreSnapshot.measurements[i];

            if (measurement?.key && measurement.key !== items[i]?.key) {
                return false;
            }
        }

        return true;
    }, [items, scrollRestoreSnapshot]);

    const canRestoreScroll = useMemo(() => {
        if (!shouldRestoreScroll) {
            return false;
        }

        return isScrollRestoreSnapshotValid;
    }, [isScrollRestoreSnapshotValid, shouldRestoreScroll]);

    const { current: elementByIndexWeakMap } = useRef(new WeakMap<HTMLElement, number>());

    const measurements: Measurement[] = useMemo(() => {
        if (canRestoreScroll) {
            return scrollRestoreSnapshot?.measurements ?? [];
        }

        return [];
    }, [scrollRestoreSnapshot, canRestoreScroll]);

    const saveMeasurement = (node: HTMLElement, result: number) => {
        const index = elementByIndexWeakMap.get(node);

        if (index === undefined) {
            return;
        }

        const key = items[index]?.key;

        if (!key) {
            return;
        }

        measurements[index] = { key, size: result };
    };

    function getNonWindowScrollElement() {
        if (isUsingContainer) {
            return parentRef.current;
        }

        return getVirtualScrollWindowElement();
    }

    const getScrollElement = () =>
        shouldUseWindowAsScrollableElementRef.current ? window : getNonWindowScrollElement();

    const virtualizer = useVirtualizer<ScrollElement, HTMLElement>({
        count: hasNextPage ? items.length + 1 : items.length,
        estimateSize: (index) => {
            const cachedSize = canRestoreScroll
                ? scrollRestoreSnapshot?.measurements?.[index]?.size
                : undefined;

            if (cachedSize) {
                return cachedSize;
            }

            return getItemSize(index);
        },
        overscan,
        observeElementRect: shouldUseWindowAsScrollableElement
            ? (observeWindowRect as typeof observeElementRect)
            : observeElementRect,
        getScrollElement,
        getItemKey: (index) => items[index]?.key ?? `fallback-prefix-${index}`,
        observeElementOffset: shouldUseWindowAsScrollableElement
            ? observeWindowOffset
            : observeElementOffset,
        measureElement: (node, observer, instance) => {
            const result = measureElement(node, observer, instance);

            saveMeasurement(node, result);
            return result;
        },
    });

    const virtualItems = virtualizer.getVirtualItems();

    const virtualizeChildNode = (childNode: Node, index: number) => {
        const node = childNode as HTMLElement;

        const virtualItem = virtualItems[index];

        if (!virtualItem) {
            return;
        }

        const dataSetValue = `${virtualItem.index}`;
        if (node.dataset.index !== dataSetValue) {
            node.setAttribute('data-index', dataSetValue);
        }
        elementByIndexWeakMap.set(node, virtualItem.index);

        const style = `position:absolute;top:0;left:0;width:100%;transform:translateY(${virtualItem.start}px)`;

        const remeasure = () =>
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (virtualizer.getVirtualItems()[index]?.key === virtualItem.key) {
                        virtualizer.measureElement(node);
                    }
                });
            });

        if (style !== node.getAttribute('style')) {
            node.setAttribute('style', style);
            virtualizer.measureElement(node);
            remeasure();
            return;
        }

        if (virtualItem.size !== node.getBoundingClientRect().height) {
            remeasure();
        }
    };

    const virtualize = () => {
        // Resolve the element that acts as the spacer container. If a selector is provided, we find it relative to parent; otherwise we use the ref.
        const spacerNode = spacerSelector
            ? getParentNode(parentRef.current, spacerSelector)
            : spacerRef.current;

        if (!spacerNode) {
            return;
        }

        // Apply spacer styles to the spacer container element (its height represents the total virtual list size)
        const spacerStyle = getSpacerElementStyle(virtualizer.getTotalSize());
        if (spacerNode.getAttribute('style') !== spacerStyle) {
            spacerNode.setAttribute('style', spacerStyle);
        }

        // Apply parent viewport styles when maxHeight is defined
        if (height && parentRef.current) {
            const parentStyle = getParentViewportStyle(height);
            if (parentStyle && parentRef.current.getAttribute('style') !== parentStyle) {
                parentRef.current.setAttribute('style', parentStyle);
            }
        }

        spacerNode.childNodes.forEach(virtualizeChildNode);
    };

    useLayoutEffect(virtualize);

    useLayoutEffect(() => {
        if (!canRestoreScroll) {
            return;
        }

        const scrollElement = getScrollElement();
        const offset = scrollRestoreSnapshot?.offset;

        if (!scrollElement || !offset) {
            return;
        }

        requestIdleCallback(() => {
            scrollElement.scrollTo({
                top: offset,
                behavior: smoothScrollRestoration ? 'smooth' : 'auto',
            });
        });

        // we want to react only on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        return () => {
            if (onOffsetSave) {
                cache.set(scrollRestorationKey, {
                    offset: onOffsetSave(),
                    measurements,
                });
                return;
            }

            const scrollElement = getScrollElement();

            if (!scrollElement) {
                return;
            }

            cache.set(scrollRestorationKey, {
                offset: getOffset(scrollElement),
                measurements,
            });
        };
        // we want to react only on unmount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isScrollRestoreSnapshotValid) {
            cache.delete(scrollRestorationKey);
        }
    }, [cache, isScrollRestoreSnapshotValid, scrollRestorationKey]);

    useEffect(() => {
        if (!shouldUseWindowAsScrollableElement) {
            return NOOP;
        }

        const onScroll = () => {
            setScrollOffsetRef.current?.(getWindowScrollOffset(parentRef.current), true);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [shouldUseWindowAsScrollableElement]);

    useEffect(() => {
        if (!hasNextPage) {
            return;
        }

        const lastItem = virtualItems[virtualItems.length - 1];

        if (!lastItem) {
            return;
        }

        if (isNextPageLoading) {
            return;
        }

        if (!hasNextPage) {
            return;
        }

        if (lastItem.index > items.length - 1) {
            loadMoreItems?.();
        }
    }, [hasNextPage, loadMoreItems, items.length, isNextPageLoading, virtualItems]);

    return {
        parentRef,
        spacerRef,
        virtualItems,
    };
}
